import { Injectable } from '@angular/core';

import { Select } from '@ngxs/store';
import { AccountService } from 'app/core';
import { Account, FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { Observable, of, zip } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ObjectsManagerService } from '../objects-manager/services/objects-manager.service';
import { ObjectsManagerState } from '../objects-manager/state/state';
import {
  LockableFieldDef,
  LockableFieldsResolverDataBase,
  LockableFieldType,
  LockedField,
} from './locked-fields.types';
import { LockedFieldService } from './services/locked-fields.service';

@Injectable()
export class LockableFieldsResolver {
  private allowedTypes = [
    RelatedToType.Project,
    RelatedToType.Property,
    RelatedToType.Task,
    RelatedToType.Company,
    RelatedToType.Contact,
  ];
  @Select(ObjectsManagerState.getForMenu) customObjects$: Observable<CustomObject[]>;

  private get account(): Account {
    return this.accountService.getCurrentAccount();
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly lockedFieldService: LockedFieldService,
    private readonly objectsManagerService: ObjectsManagerService,
  ) {}

  resolve(): Observable<LockableFieldsResolverDataBase> {
    return this.accountService.getFieldDefs().pipe(
      switchMap((fieldDefs: FieldDef[]) =>
        zip(
          of(fieldDefs),
          this.lockedFieldService.getByAccountId(this.account.id),
          this.objectsManagerService.get(Number(this.account.id)),
        ),
      ),
      map(([fieldDefs, lockedFields, customObjects]: [FieldDef[], LockedField[], CustomObject[]]) => {
        const lockableFields = fieldDefs.map((fieldDef) => {
          return {
            ...fieldDef,
            lockInfo: lockedFields.filter(
              (lockedField) => lockedField.name === fieldDef.name && lockedField.klass === fieldDef.related_to_type,
            ),
          } as LockableFieldDef;
        });

        return {
          lockableFields,
          customObjects: customObjects.map(({ id, name, label }) => ({ id, name, label })),
          relatedToTypes: [...new Set([...lockableFields.map(({ related_to_type }) => related_to_type)])].map(
            (type: RelatedToType) => {
              return {
                type: type,
                canInherit: type == RelatedToType.Project,
                canLock: this.allowedTypes.includes(type),
              } as LockableFieldType;
            },
          ),
        } as LockableFieldsResolverDataBase;
      }),
    );
  }
}
